import { HALL_RANK_COUNT } from '../constants';
import { getInstanceDefinition } from '../cards/cardLookup';
import { getHandler } from '../cards/effectRegistry/index';
import { consumeCircleOfProtection } from '../cards/effectHelpers';

export function createEmptyHall(boardSide) {
  return new Array(HALL_RANK_COUNT[boardSide]).fill(null);
}

// Root of Corruption, Circle of Protection: "Cancel the next Raid, Breach,
// or Aftermath effect that takes place on your turn and destroy this
// card." Only Raid/Breach fire through this file (Aftermath is handled the
// same way in combat/resolveCombat.js) - checked right before a handler is
// actually about to run, per-ability-line, so a card with no registered
// handler for this timing never burns a copy for nothing.
function resolveTimedHandlers(state, instanceId, timing, extraCtx) {
  const def = getInstanceDefinition(state, instanceId);
  let nextState = state;
  (def.abilityText[timing] || []).forEach((entry, abilityIndex) => {
    const handler = getHandler(def.id, timing, abilityIndex);
    if (!handler) return; // not yet implemented - silently skipped
    if (extraCtx.playerId) {
      const { state: afterCancel, cancelled } = consumeCircleOfProtection(nextState, extraCtx.playerId);
      nextState = afterCancel;
      if (cancelled) return;
    }
    nextState = handler(nextState, { instanceId, choices: {}, ...extraCtx });
  });
  return nextState;
}

// Stramst (bearer-stramst) has a Global effect: "When refilling the hall,
// Stramst does not move forward to an empty rank until the dungeon is
// depleted. Instead, refill the empty rank in front of him." - i.e. his
// position is pinned; refills happen around him, not by sliding him forward,
// until the deck runs dry (at which point there's nothing left to refill with
// anyway and normal compaction resumes).
function findStramstRank(state) {
  return state.dungeonHall.findIndex((instanceId) => (
    instanceId && getInstanceDefinition(state, instanceId).id === 'bearer-stramst'
  ));
}

// Caverns of Bane, Thorn Caltrops: "Cancel Raid effects." A Weapon's whole
// card text (not just its Attack line) only functions while equipped - see
// the same convention in combat/attackMath.js's contributesTraits - so this
// checks the active player's equipped weapons, not just their hand.
function isRaidCancelledByThornCaltrops(state, playerId) {
  if (!playerId) return false;
  const player = state.players.find((p) => p.id === playerId);
  if (!player) return false;
  return Object.values(player.equippedWeapons).flat().some((weaponId) => getInstanceDefinition(state, weaponId).id === 'village-thorn-caltrops');
}

// Fills every empty rank from the dungeon deck's top, outermost rank first
// (rulebook, "Refilling the hall"), firing Raid on every freshly-revealed
// card and Breach on whatever newly occupies rank 1 (index 0) - EXCEPT during
// initial setup, when the rulebook explicitly says to ignore both ("ignore
// Raid text on monsters starting the game already placed in the hall";
// "[Breach] ignored if a monster... starts the game already in rank 1").
// Callers doing the initial fill (createGameState.js) must pass
// { isInitialSetup: true }; mid-game refills (resolveCombat.js) pass the
// active playerId instead so Raid/Breach handlers that need a decision-maker
// have one (rulebook: "the active player decides").
export function compactAndRefill(state, { isInitialSetup = false, playerId } = {}) {
  const rankCount = state.dungeonHall.length;
  const stramstRank = findStramstRank(state);
  const rank0OccupantBefore = state.dungeonHall[0];

  let remaining;
  let pinnedStramstId = null;
  if (stramstRank !== -1) {
    pinnedStramstId = state.dungeonHall[stramstRank];
    remaining = state.dungeonHall.filter((id, i) => id && i !== stramstRank);
  } else {
    remaining = state.dungeonHall.filter(Boolean);
  }

  let dungeonDeck = [...state.dungeonDeck];
  let workingState = state;
  const newHall = [...remaining];
  const revealedIds = [];
  const targetLength = pinnedStramstId ? rankCount - 1 : rankCount;
  while (newHall.length < targetLength && dungeonDeck.length > 0) {
    const revealed = dungeonDeck.shift();
    newHall.push(revealed);
    revealedIds.push(revealed);
  }
  while (newHall.length < targetLength) newHall.push(null);

  let finalHall;
  if (pinnedStramstId && dungeonDeck.length > 0) {
    // Stramst holds his rank; every other card compacts around him.
    finalHall = [];
    let cursor = 0;
    for (let rank = 0; rank < rankCount; rank += 1) {
      if (rank === stramstRank) finalHall.push(pinnedStramstId);
      else { finalHall.push(newHall[cursor] ?? null); cursor += 1; }
    }
  } else if (pinnedStramstId) {
    // Deck depleted - Stramst resumes normal compaction alongside everyone else.
    const all = [pinnedStramstId, ...remaining].filter(Boolean);
    finalHall = [...all];
    while (finalHall.length < rankCount) finalHall.push(null);
  } else {
    finalHall = newHall;
  }

  workingState = { ...workingState, dungeonHall: finalHall, dungeonDeck };

  if (!isInitialSetup) {
    const raidCancelled = isRaidCancelledByThornCaltrops(state, playerId);
    for (const instanceId of revealedIds) {
      if (!raidCancelled) workingState = resolveTimedHandlers(workingState, instanceId, 'raid', { playerId });
    }

    const rank0OccupantAfter = workingState.dungeonHall[0];
    if (rank0OccupantAfter && rank0OccupantAfter !== rank0OccupantBefore) {
      workingState = resolveTimedHandlers(workingState, rank0OccupantAfter, 'breach', { playerId });
    }
  }

  return workingState;
}
